import React, { useState, useEffect, useRef } from "react";
import Lottie from "react-lottie";
import "./styles.scss";

// Images from public folder
const backgroundLayer = "/2025/hero/images/layer1.png";
const erpImage = "/2025/hero/images/ERP.png";

// Icons
const energyIcon = "/2025/hero/icons/energy.svg";
const shieldIcon = "/2025/hero/icons/shield.svg";
const productionIcon = "/2025/hero/icons/production.svg";
const constructionIcon = "/2025/hero/icons/construction.svg";
const buildingIcon = "/2025/hero/icons/building.svg";
const tradeIcon = "/2025/hero/icons/trade.svg";

// Custom hook for scroll animations
const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        if (options.once !== false) {
          observer.unobserve(entry.target);
        }
      } else if (options.once === false) {
        setIsInView(false);
      }
    }, {
      threshold: options.threshold || 0.2,
      rootMargin: options.rootMargin || '0px'
    });

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  return [ref, isInView];
};

export function Hero() {
  const [animationData, setAnimationData] = useState(null);
  
  // Animation refs - rootMargin negative value delays trigger until element is more centered
  const [contentRef, contentInView] = useInView({ threshold: 0.5, rootMargin: '-100px 0px' });
  const [imageRef, imageInView] = useInView({ threshold: 0.4, rootMargin: '-100px 0px' });
  const [cardsRef, cardsInView] = useInView({ threshold: 0.3, rootMargin: '-80px 0px' });

  useEffect(() => {
    fetch("/2025/lottie/heroAnimationDecoration.json")
      .then((response) => response.json())
      .then((data) => setAnimationData(data))
      .catch((error) => console.error("Error loading animation:", error));
  }, []);

  const lottieOptions = {
    loop: true,
    autoplay: true,
    animationData: animationData,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  return (
    <section className="hero-section">
      {/* Top Section - 830px */}
      <div className="hero-top">
        {/* Background */}
        <div className="hero-background">
          <img src={backgroundLayer} alt="" className="hero-bg-image" />
        </div>

        {/* Animated Christmas decorations */}
        <div className="hero-animation-decoration">
          {animationData && <Lottie options={lottieOptions} />}
        </div>

        {/* Main content card */}
        <div className="hero-card">
          <div className="hero-card-content">
            <div className="hero-logo">
              <span className="hero-logo-text">FidWeb</span>
              <div className="hero-logo-underline" />
            </div>
            <p className="hero-message">Екипът на FIDWEB стои гордо зад изградените продукти със стотици хиляди потребители и гледаме отговорно към предстоящите интересни предизвикателства. С надежда за здраве и с енергия за съвместни нови постижения по света и у нас Ви пожелаваме отлична 2026-та година!</p>
          </div>
        </div>
      </div>

      {/* Bottom Section - 804px - ERP Section */}
      <div className="hero-bottom">
        <div className="erp-container">
          {/* Left side - Text content */}
          <div 
            ref={contentRef}
            className={`erp-content animate-on-scroll ${contentInView ? 'is-visible' : ''}`}
          >
            <h2 className="erp-heading animate-item">
              <span>ERP решения</span>
              <span>с мисъл за бъдещето</span>
            </h2>
            <p className="erp-description animate-item">
              През 2025 продължихме да развиваме ERP решения, създадени специално за нуждите на бизнеса в различни индустрии. Вместо универсални платформи, ние изграждаме системи, които отразяват реалните процеси, регулации и динамика на конкретния сектор.
            </p>
          </div>

          {/* Right side - ERP Image */}
          <div 
            ref={imageRef}
            className={`erp-image animate-slide-right ${imageInView ? 'is-visible' : ''}`}
          >
            <img src={erpImage} alt="ERP Solutions" />
          </div>
        </div>

        {/* Industry Cards Grid - 2 rows x 3 columns */}
        <div 
          ref={cardsRef}
          className={`erp-cards-grid animate-stagger ${cardsInView ? 'is-visible' : ''}`}
        >
          <div className="erp-card">
            <img src={energyIcon} alt="" className="erp-card-icon" />
            <span className="erp-card-title">Енергетика</span>
          </div>
          <div className="erp-card">
            <img src={shieldIcon} alt="" className="erp-card-icon" />
            <span className="erp-card-title">Застраховане</span>
          </div>
          <div className="erp-card">
            <img src={productionIcon} alt="" className="erp-card-icon" />
            <span className="erp-card-title">Производство</span>
          </div>
          <div className="erp-card">
            <img src={constructionIcon} alt="" className="erp-card-icon" />
            <span className="erp-card-title">Строителство</span>
          </div>
          <div className="erp-card">
            <img src={buildingIcon} alt="" className="erp-card-icon" />
            <span className="erp-card-title">Недвижими имоти</span>
          </div>
          <div className="erp-card">
            <img src={tradeIcon} alt="" className="erp-card-icon" />
            <span className="erp-card-title">Търговия</span>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Hero;
